/**
 * /apps — 앱 고르기 (groupcrew 셸 승계).
 *
 * 데스크톱은 서버가 그린 격자(#appsDesk)뿐이다 (spec 2026-09-29 §4.1): 사내 서비스 타일은 새 창으로,
 * 데스크톱 설치 앱의 탭 틀(/workspace) 안이면 틀의 탭으로 열린다(a[data-ws-app] — workspace-frame.js).
 * 여러 서비스를 탭으로 오가는 일은 탭 틀이 맡는다.
 *
 * 폰(≤768px)은 여기의 프레임 엔진 + modules/apps-mobile.js(런처 · 전체 화면 · 전환 시트)다:
 * - 앱마다 iframe 하나를 두고 숨길 뿐 지우지 않아 상태가 산다
 * - 크로스오리진 iframe 의 내부 이동은 서비스에 주입된 브리지(/static/iframe-bridge.js,
 *   server/apps_bridge.go)가 postMessage 로 알려줄 때만 안다 — 그 보고로 프레임별 이력(←/→)을 쌓는다
 * - 열린 앱 · 활성 앱 · 각 앱의 마지막 주소는 localStorage 로 되살린다
 * 엔진은 조작 API 와 #appsContainer 의 apps:changed 이벤트만 넘긴다 (spec 2026-09-24-apps-mobile §2.2).
 * 앱 목록(이름 · 주소 · 여는 방식)은 런처 카드(#appLauncher .app-ntp-card)의 data-app-* 다.
 *
 * 사내망 전용 서비스 (migration 131): 서버가 「사내망 밖」이라고 판정했으면(#appsContainer[data-offnet]) 그 서비스는
 * 여는 자리에 iframe 대신 안내 카드(modules/apps-offnet.js)를 둔다 — 「그래도 열기」를 누르면 그때 프레임을 만든다.
 * 새 창으로 여는 서비스와 데스크톱 타일은 확인 창으로 묻는다(탭 틀 안의 타일은 틀이 그 탭에 같은 카드를 그린다).
 */
import { setupAppsMobile, isPhone, phoneRestoreIds } from './apps-mobile.js';
import { postToShell } from './workspace-frame.js';
import { appConfirm } from './confirm-dialog.js';
import { offnetAllowed, allowOffnet, offnetCard } from './apps-offnet.js';

const STORE_KEY = 'portalApps';
// 프레임당 이력 최대 길이 (groupcrew 셸 iframe.tsx 와 같은 값).
const HISTORY_MAX = 50;
// 셸이 지시한 이동이 "진행 중"으로 남아 있을 수 있는 최대 시간(ms). load 가
// 끝내 오지 않는 프레임(차단·다운로드 응답 등)에서 보고가 영영 막히지 않게 하는
// 안전판일 뿐, 정상 경로는 load 이벤트가 끝낸다.
const NAV_PENDING_MS = 15000;

// 사내망 안내 문구 — #appsContainer 의 data-l-offnet-* (서버가 번역해 싣는다).
function offnetLabels(container) {
    const d = container.dataset;
    return { title: d.lOffnetTitle, body: d.lOffnetBody, open: d.lOffnetOpen, back: d.lOffnetBack };
}

// 사내망 밖에서 사내망 전용 서비스를 열기 전에 묻는다 (새 창 · 이 탭 이동으로 여는 경우).
function confirmOffnet(container, name) {
    const L = offnetLabels(container);
    return appConfirm({ title: L.title, message: name, detail: L.body, okLabel: L.open });
}

function inShell() {
    return document.documentElement.hasAttribute('data-workspace-frame');
}

// 데스크톱의 옛 딥링크 /apps?open=<앱 id>: 탭 틀 안이면 그 서비스 탭을 열고, 아니면 이 탭이 그 서비스로 간다
// (새 창은 사용자 몸짓 없이는 팝업 차단에 걸린다).
function setupDesktopLauncher(frames, container) {
    // 사내망 밖의 사내망 전용 타일: 새 창으로 열기 전에 묻는다. 탭 틀 안의 iframe 서비스는 틀이 그 탭에 안내 카드를
    // 그리므로 묻지 않는다(두 번 묻지 않게). 몸짓 안에서 연 확인 창의 「그래도 열기」도 몸짓이라 새 창이 막히지 않는다.
    const desk = document.getElementById('appsDesk');
    if (desk) {
        desk.addEventListener('click', (e) => {
            const a = e.target.closest('a.apps-desk-app[data-offnet]');
            if (!a || e.defaultPrevented || offnetAllowed(a.dataset.appId)) return;
            if (inShell() && a.dataset.wsApp) return;
            e.preventDefault();
            confirmOffnet(container, a.dataset.appName || a.textContent.trim()).then((ok) => {
                if (!ok) return;
                allowOffnet(a.dataset.appId);
                window.open(a.href, '_blank', 'noopener');
            });
        });
    }
    const want = frames.dataset.openApp;
    if (!want) return;
    const a = document.querySelector('#appsDesk a.apps-desk-app[data-app-id="' + CSS.escape(want) + '"]');
    if (!a) return;
    if (a.dataset.wsApp && postToShell('ws:app', { id: a.dataset.wsApp })) {
        // 이 탭은 격자로 남는다 — 다시 불러도 또 열지 않게 ?open= 을 지운다.
        try { window.history.replaceState(window.history.state, '', '/apps'); } catch (_) { /* 무해 */ }
        return;
    }
    if (a.dataset.offnet && !offnetAllowed(a.dataset.appId)) {
        // 사내망 밖: 묻고 나서 간다. 그만두면 격자에 남는다(다시 불러도 또 묻지 않게 ?open= 을 지운다).
        try { window.history.replaceState(window.history.state, '', '/apps'); } catch (_) { /* 무해 */ }
        confirmOffnet(container, a.dataset.appName || a.textContent.trim()).then((ok) => {
            if (!ok) return;
            allowOffnet(a.dataset.appId);
            window.location.replace(a.href);
        });
        return;
    }
    window.location.replace(a.href);
}

export function setupAppLauncher() {
    const frames = document.getElementById('appFrames');
    const container = document.getElementById('appsContainer');
    if (!frames || !container) return;
    const phoneAtStart = isPhone();
    if (!phoneAtStart) setupDesktopLauncher(frames, container);
    // 서버의 판정: 사내망 밖이다 (대역이 없으면 판정하지 않아 경고도 없다)
    const offnet = container.dataset.offnet === '1';

    // apps:changed — 폰 화면(apps-mobile.js)이 얇은 줄·격자의 열림 표시를 다시 그리는 신호.
    // 활성·닫기·이동(←/→·브리지·로드) 때마다 #appsContainer 에서 올린다.
    function emitChange(type, id) {
        container.dispatchEvent(new CustomEvent('apps:changed', { detail: { type, id: id || '' } }));
    }

    const launcher = document.getElementById('appLauncher');
    const cards = launcher ? Array.from(launcher.querySelectorAll('.app-ntp-card[data-app-id]')) : [];
    // 표시 이름: 이름이 없는 항목도 절대 "undefined" 로 그리지 않는다.
    const appName = (el) => (el && (el.dataset.appName || el.dataset.appUrl || el.dataset.appId)) || '';
    const byID = {};
    cards.forEach((el) => {
        byID[el.dataset.appId] = el;
    });

    // 브리지 메시지를 받아줄 오리진 화이트리스트: 등록된 앱 URL 의 오리진들.
    // (앱이 프레임 안에서 우리 호스트들 사이를 오갈 수 있으므로 프레임별 오리진
    //  하나로 고정하지 않고 앱 목록 전체로 만든다)
    const appOrigins = new Set();
    cards.forEach((el) => {
        try {
            appOrigins.add(new URL(el.dataset.appUrl).origin);
        } catch (e) {
            /* 잘못된 URL 은 화이트리스트에 넣지 않는다 */
        }
    });
    // 열린 앱 id (연 순서) 와 활성 앱
    const openOrder = [];
    let activeAppId = '';
    // 프레임별 이동 이력: { appId: { stack: [url…], idx, pending } }
    const histories = {};
    // 복원용으로 저장해둔 마지막 URL — 프레임을 처음 만들 때 한 번만 쓴다.
    let pendingUrls = {};

    const frameOf = (id) => frames.querySelector('.app-frame[data-app-id="' + CSS.escape(id) + '"]');
    // 안내 카드(사내망 밖)는 .app-frame 이지만 iframe 이 아니다 — 이동·새로고침은 프레임에만 한다.
    const isLive = (frame) => !!frame && frame.tagName === 'IFRAME';
    // 이 앱을 열기 전에 사내망 밖이라고 알려야 하는가
    const needsWarn = (id) => offnet && !!byID[id] && byID[id].dataset.appInternal === '1' && !offnetAllowed(id);

    // ── 상태 저장/복원 ──
    function saveState() {
        // 각 앱이 지금 보고 있는 주소도 같이 저장한다 — 새로고침하면 앱의 첫
        // 화면이 아니라 보고 있던 화면으로 돌아온다.
        const urls = {};
        frames.querySelectorAll('.app-frame').forEach((f) => {
            if (f.dataset.currentUrl) urls[f.dataset.appId] = f.dataset.currentUrl;
        });
        try {
            localStorage.setItem(STORE_KEY, JSON.stringify({ open: openOrder.slice(), active: activeAppId, urls }));
        } catch (e) {
            /* 무해 */
        }
    }

    function setEmptyHint() {
        const state = document.getElementById('appEmptyState');
        if (state) state.style.display = openOrder.length ? 'none' : 'flex';
    }

    function activeFrame() {
        return frames.querySelector('.app-frame.active');
    }

    // ── 프레임별 이동 이력 (브리지가 알려준 URL + 셸이 지시한 이동) ──
    function historyOf(id) {
        if (!histories[id]) histories[id] = { stack: [], idx: -1, pending: null };
        return histories[id];
    }

    // ── 셸이 지시한 이동의 "진행 중" 표시 ──
    // 프레임 하나의 contentWindow 는 문서가 바뀌어도 같은 객체라, 셸이 이미 떠나온
    // 문서가 늦게 보내온 메시지도 event.source 검사를 통과한다. 그래서 프레임마다
    // 이동 세대(navEpoch)를 찍고 목표 URL 을 기억해 두었다가, 다른 목표가 진행
    // 중일 때 온 보고는 버린다 (←를 연달아 두 번 누르면 첫 번째 목표의 문서가
    // 보낸 메아리가 두 번째 이동 뒤에 도착해 주소를 되돌리던 문제).
    function beginNav(frame, url) {
        const epoch = (Number(frame.dataset.navEpoch) || 0) + 1;
        frame.dataset.navEpoch = String(epoch);
        historyOf(frame.dataset.appId).pending = { epoch, url, at: Date.now() };
    }

    function pendingNav(frame) {
        const h = historyOf(frame.dataset.appId);
        if (!h.pending) return null;
        // 세대가 어긋났거나(다른 이동이 덮어썼다) 너무 오래됐으면 없는 셈 친다.
        if (h.pending.epoch !== (Number(frame.dataset.navEpoch) || 0) || Date.now() - h.pending.at > NAV_PENDING_MS) {
            h.pending = null;
        }
        return h.pending;
    }

    function settleNav(id) {
        historyOf(id).pending = null;
    }

    function pushHistory(id, url) {
        const h = historyOf(id);
        if (h.stack[h.idx] === url) return;
        h.stack = h.stack.slice(0, h.idx + 1);
        h.stack.push(url);
        if (h.stack.length > HISTORY_MAX) h.stack.shift();
        h.idx = h.stack.length - 1;
    }

    function updateNavButtons() {
        const frame = activeFrame();
        emitChange('nav', frame ? frame.dataset.appId : '');
    }

    function activate(id) {
        if (!openOrder.includes(id)) return;
        activeAppId = id;
        frames.querySelectorAll('.app-frame').forEach((f) => f.classList.toggle('active', f.dataset.appId === id));
        updateNavButtons();
        saveState();
        emitChange('activate', id);
    }

    function close(id) {
        const i = openOrder.indexOf(id);
        if (i < 0) return;
        openOrder.splice(i, 1);
        const frame = frameOf(id);
        if (frame) frame.remove();
        delete histories[id]; // 앱을 닫으면 그 앱의 이동 이력도 버린다
        if (activeAppId === id) {
            activeAppId = '';
            if (openOrder.length) activate(openOrder[0]);
            else updateNavButtons();
        }
        setEmptyHint();
        saveState();
        emitChange('close', id);
    }

    const loadBar = document.getElementById('appLoadBar');
    function setLoadBar(on) {
        if (!loadBar) return;
        if (on) {
            loadBar.classList.add('on');
        } else {
            loadBar.classList.add('done');
            setTimeout(() => loadBar.classList.remove('on', 'done'), 250);
        }
    }

    function markLoading(id) {
        const frame = frameOf(id);
        if (frame) frame.dataset.loading = '1';
        setLoadBar(true);
    }

    function reloadApp(id) {
        const frame = frameOf(id);
        if (!isLive(frame)) return;
        markLoading(id);
        try {
            frame.contentWindow.location.reload();
        } catch (e) {
            // 크로스오리진이면 마지막 URL로 재로드
            const url = frame.dataset.currentUrl || frame.src;
            beginNav(frame, url);
            frame.src = url;
        }
    }

    // keepHistory: 이력 안에서 움직이는 중(←/→)이라 스택을 건드리면 안 된다.
    function navigate(frame, url, keepHistory) {
        frame.dataset.currentUrl = url;
        if (!keepHistory) pushHistory(frame.dataset.appId, url);
        if (!isLive(frame)) {
            // 안내 카드: 「그래도 열기」 때 이 주소로 연다
            updateNavButtons();
            saveState();
            return;
        }
        beginNav(frame, url);
        markLoading(frame.dataset.appId);
        frame.src = url;
        updateNavButtons();
        saveState();
    }

    function currentUrlOf(id) {
        const frame = frameOf(id);
        if (frame) return frame.dataset.currentUrl || '';
        return byID[id] ? byID[id].dataset.appUrl : '';
    }

    // 프레임을 처음 만들 때의 주소: 직전 세션에서 보고 있던 URL(복원용, 한 번만)
    // 이 있으면 그것, 없으면 앱에 등록된 URL.
    function startUrlFor(id) {
        const saved = pendingUrls[id];
        delete pendingUrls[id];
        if (typeof saved === 'string' && /^https?:\/\//i.test(saved)) return saved;
        return byID[id].dataset.appUrl;
    }

    // 앱의 iframe 을 만든다 (붙이는 것은 부르는 쪽): startUrl 에서 시작하고 로드마다 주소·로딩 표시를 맞춘다.
    function createFrame(id, startUrl) {
        const card = byID[id];
        const frame = document.createElement('iframe');
        frame.className = 'app-frame';
        frame.dataset.appId = id;
        frame.dataset.currentUrl = startUrl;
        frame.dataset.loading = '1';
        beginNav(frame, startUrl);
        frame.src = startUrl; // 첫 오픈 시에만 로드
        setLoadBar(true);
        frame.title = appName(card);
        frame.addEventListener('load', () => {
            delete frame.dataset.loading;
            setLoadBar(false);
            // 이동이 끝났다 — 이제부터 오는 보고는 이 문서의 것이다
            // (리다이렉트로 도착한 주소도 여기서부터 받아들인다).
            settleNav(id);
            try {
                // 같은 오리진일 때만 실제 URL 확인 가능
                const href = frame.contentWindow.location.href;
                if (href && href !== 'about:blank') frame.dataset.currentUrl = href;
            } catch (e) {
                /* 크로스오리진 — 마지막으로 지정한 URL 유지 */
            }
            updateNavButtons();
        });
        return frame;
    }

    // 사내망 밖의 안내 카드 (iframe 자리). 「목록으로」는 폰 화면(apps-mobile.js)의 [data-appm-close] 로 — 앱을 닫고
    // 런처로 돌아간다. 「그래도 열기」는 이 탭에 기억하고 그 자리에 프레임을 만든다.
    function createOffnetCard(id, startUrl) {
        const card = byID[id];
        const { root, back, open: openBtn } = offnetCard({
            className: 'app-frame app-offnet',
            name: appName(card),
            url: startUrl,
            labels: offnetLabels(container),
        });
        root.dataset.appId = id;
        root.dataset.currentUrl = startUrl;
        back.setAttribute('data-appm-close', id);
        openBtn.addEventListener('click', () => {
            allowOffnet(id);
            const holder = frameOf(id);
            if (!holder || isLive(holder)) return;
            const frame = createFrame(id, holder.dataset.currentUrl || startUrl);
            frame.classList.toggle('active', activeAppId === id);
            holder.replaceWith(frame);
            updateNavButtons();
            saveState();
        });
        return root;
    }

    function open(id) {
        const card = byID[id];
        if (!card) return;
        if (card.dataset.appMode === 'tab') {
            if (needsWarn(id)) {
                confirmOffnet(container, appName(card)).then((ok) => {
                    if (!ok) return;
                    allowOffnet(id);
                    window.open(card.dataset.appUrl, '_blank', 'noopener');
                });
                return;
            }
            window.open(card.dataset.appUrl, '_blank', 'noopener');
            return;
        }
        if (!openOrder.includes(id)) {
            openOrder.push(id);
            const startUrl = startUrlFor(id);
            histories[id] = { stack: [startUrl], idx: 0, pending: null };
            frames.appendChild(needsWarn(id) ? createOffnetCard(id, startUrl) : createFrame(id, startUrl));
        }
        setEmptyHint();
        activate(id);
    }

    function frameHistory(step) {
        const frame = activeFrame();
        if (!frame) return;
        const h = historyOf(frame.dataset.appId);
        const next = h.idx + step;
        if (next >= 0 && next < h.stack.length) {
            // 브리지가 알려준(또는 셸이 지시한) 이력이 있으면 셸이 직접 이동한다.
            // navigate() 가 이 이동을 "진행 중"으로 표시하므로, 떠나온 문서가 늦게
            // 보내오는 메아리는 아래 message 리스너에서 버려진다.
            h.idx = next;
            navigate(frame, h.stack[next], true);
            return;
        }
        if (h.stack.length > 1) return; // 그 방향에 더 갈 곳이 없다
        try {
            // 브리지 없는 서비스의 폴백: 동일 오리진이면 프레임의 history 를
            // 직접 민다. 크로스오리진이면 SecurityError — 그 경우 아무 일도 하지 않는다.
            frame.contentWindow.history.go(step);
        } catch (e) { /* cross-origin */ }
    }

    // ── iframe 브리지 (server/apps_bridge.go → /static/iframe-bridge.js) ──
    // 서비스에 주입된 브리지가 프레임 안의 주소·제목 변화를 postMessage 로
    // 알려준다. 크로스오리진 프레임의 내부 이동을 알 수 있는 유일한 통로다.
    window.addEventListener('message', (e) => {
        const data = e.data;
        if (!data || typeof data !== 'object' || typeof data.type !== 'string') return;
        if (data.type !== 'iframe-url-change' && data.type !== 'iframe-navigate-to-app') return;
        // 1) 실제로 우리 프레임 중 하나가 보낸 것인가
        const frame = Array.from(frames.querySelectorAll('.app-frame')).find((f) => f.contentWindow === e.source);
        if (!frame) return;
        // 2) 등록된 앱의 오리진인가 (프레임이 다른 호스트로 이동했을 수 있다)
        if (!appOrigins.has(e.origin)) return;
        if (typeof data.url !== 'string' || !data.url) return;
        let target;
        try {
            target = new URL(data.url, frame.dataset.currentUrl || e.origin);
        } catch (err) {
            return;
        }
        if (target.protocol !== 'http:' && target.protocol !== 'https:') return;

        if (data.type === 'iframe-url-change') {
            const id = frame.dataset.appId;
            // 3) 셸이 지시한 이동이 진행 중이라면, 그 목표에 대한 보고만 받는다.
            //    (다른 목표가 진행 중 = 사용자가 이미 떠난 문서의 뒤늦은 메아리)
            const pending = pendingNav(frame);
            if (pending) {
                if (pending.url !== target.href) return;
                settleNav(id);
            }
            frame.dataset.currentUrl = target.href;
            pushHistory(id, target.href);
            // 페이지 제목은 참고용으로만 둔다 (앱이 보낸 문자열 — 속성으로만 다룬다)
            if (typeof data.title === 'string' && data.title) frame.dataset.pageTitle = data.title.slice(0, 200);
            updateNavButtons();
            saveState();
            return;
        }

        // iframe-navigate-to-app: 다른 앱으로 가는 링크를 눌렀다 →
        // 그 앱의 프레임에서 연다 (등록된 앱이 아니면 새 창).
        const hit = cards.find((el) => {
            try {
                return new URL(el.dataset.appUrl).host === target.host;
            } catch (err) {
                return false;
            }
        });
        if (!hit || hit.dataset.appMode === 'tab') {
            window.open(target.href, '_blank', 'noopener');
            return;
        }
        open(hit.dataset.appId);
        const dest = frameOf(hit.dataset.appId);
        if (dest && dest.dataset.currentUrl !== target.href) navigate(dest, target.href);
    });

    // ── 복원 (폰으로 시작했을 때만 — 데스크톱은 격자다): localStorage(구버전 sessionStorage 폴백) → ?open= ──
    if (phoneAtStart) {
        try {
            const raw = localStorage.getItem(STORE_KEY) || sessionStorage.getItem(STORE_KEY) || '{}';
            const saved = JSON.parse(raw);
            // 마지막으로 보고 있던 주소로 복원한다 (없던 시절의 저장본에는 없다).
            if (saved.urls && typeof saved.urls === 'object') pendingUrls = saved.urls;
            const savedOpen = (saved.open || []).filter((id) => byID[id] && byID[id].dataset.appMode !== 'tab');
            // 폰은 살아 있는 앱을 셋까지만 둔다 — 새로고침에서도 가장 최근 셋만 되살린다 (spec §1.4)
            const restoreIds = phoneRestoreIds(savedOpen, saved.active || '');
            restoreIds.forEach((id) => open(id));
            if (saved.active && byID[saved.active] && restoreIds.includes(saved.active)) activate(saved.active);
            sessionStorage.removeItem(STORE_KEY);
        } catch (e) {
            /* 복원 실패는 무해 */
        }
        const wanted = frames.dataset.openApp;
        if (wanted && byID[wanted]) open(wanted);
    }
    setEmptyHint();
    updateNavButtons(); // 열린 앱이 없으면 ←/→ 는 비활성으로 시작한다

    // 폰 화면: 동작은 엔진에 두고 조작 API 만 넘긴다 (spec 2026-09-24-apps-mobile §2.2).
    // 데스크톱으로 시작한 창을 폰 폭으로 좁혀도 런처가 동작하도록 언제나 묶는다.
    const activeId = () => {
        const f = activeFrame();
        return f ? f.dataset.appId : '';
    };
    const activeHistory = () => histories[activeId()];
    setupAppsMobile({
        open,
        activate,
        close,
        back: () => frameHistory(-1),
        forward: () => frameHistory(1),
        reload: () => {
            const id = activeId();
            if (id) reloadApp(id);
        },
        openIds: () => openOrder.slice(),
        activeId,
        currentUrl: currentUrlOf,
        canBack: () => {
            const h = activeHistory();
            return !!(h && h.idx > 0);
        },
        canForward: () => {
            const h = activeHistory();
            return !!(h && h.idx >= 0 && h.idx < h.stack.length - 1);
        },
        appInfo: (id) => {
            const card = byID[id];
            return card ? { id, name: appName(card), url: card.dataset.appUrl || '', mode: card.dataset.appMode === 'tab' ? 'tab' : 'iframe' } : null;
        },
    });
}
